// Mouse Trajectory Preprocessing for FTT Analysis
// Implements standardized preprocessing to ensure data quality

interface MousePoint {
  x: number;
  y: number;
  timestamp: number;
}

interface MouseTrajectory {
  points: MousePoint[];
  startTime: number;
  endTime: number;
}

// Calculate the actual sampling rate of the trajectory
export function calculateSamplingRate(trajectory: MousePoint[]): number {
  if (trajectory.length < 2) return 0;
  
  const intervals: number[] = [];
  for (let i = 1; i < trajectory.length; i++) {
    intervals.push(trajectory[i].timestamp - trajectory[i-1].timestamp);
  }
  
  const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
  return avgInterval > 0 ? 1000 / avgInterval : 0; // Convert to Hz
}

// Resample trajectory to consistent 30Hz
export function resampleTo30Hz(trajectory: MousePoint[]): MousePoint[] {
  if (trajectory.length < 2) return trajectory;
  
  const targetInterval = 1000 / 30; // 33.33ms
  const resampled: MousePoint[] = [];
  const startTime = trajectory[0].timestamp;
  const endTime = trajectory[trajectory.length - 1].timestamp;
  
  // Linear interpolation for resampling
  for (let t = startTime; t <= endTime; t += targetInterval) {
    // Find surrounding points
    let i = 0;
    while (i < trajectory.length - 1 && trajectory[i + 1].timestamp < t) {
      i++;
    }
    
    if (i === trajectory.length - 1) {
      resampled.push(trajectory[i]);
    } else {
      const p1 = trajectory[i];
      const p2 = trajectory[i + 1];
      const ratio = (t - p1.timestamp) / (p2.timestamp - p1.timestamp);
      
      resampled.push({
        x: p1.x + (p2.x - p1.x) * ratio,
        y: p1.y + (p2.y - p1.y) * ratio,
        timestamp: t
      });
    }
  }
  
  return resampled;
}

// Count missing data points (gaps > 100ms)
export function countMissingPoints(trajectory: MousePoint[]): number {
  if (trajectory.length < 2) return 0;
  
  let missingCount = 0;
  for (let i = 1; i < trajectory.length; i++) {
    const gap = trajectory[i].timestamp - trajectory[i-1].timestamp;
    if (gap > 100) { // Gap > 100ms indicates missing data
      missingCount += Math.floor(gap / 33.33) - 1; // Expected points at 30Hz
    }
  }
  
  return missingCount;
}

// Apply rolling average smoothing
export function applyRollingAverage(
  trajectory: MousePoint[], 
  windowSizeMs: number = 100
): MousePoint[] {
  if (trajectory.length < 2) return trajectory;
  
  const smoothed: MousePoint[] = [];
  
  for (let i = 0; i < trajectory.length; i++) {
    const currentTime = trajectory[i].timestamp;
    const windowStart = currentTime - windowSizeMs / 2;
    const windowEnd = currentTime + windowSizeMs / 2;
    
    // Find points within window
    const windowPoints = trajectory.filter(
      p => p.timestamp >= windowStart && p.timestamp <= windowEnd
    );
    
    if (windowPoints.length > 0) {
      const avgX = windowPoints.reduce((sum, p) => sum + p.x, 0) / windowPoints.length;
      const avgY = windowPoints.reduce((sum, p) => sum + p.y, 0) / windowPoints.length;
      
      smoothed.push({
        x: avgX,
        y: avgY,
        timestamp: trajectory[i].timestamp
      });
    } else {
      smoothed.push(trajectory[i]);
    }
  }
  
  return smoothed;
}

// Main preprocessing function
export function preprocessMouseTrajectory(
  rawTrajectory: MousePoint[]
): MousePoint[] | null {
  if (!rawTrajectory || rawTrajectory.length < 5) {
    return null; // Too few points
  }
  
  // 1. Check sampling rate
  const samplingRate = calculateSamplingRate(rawTrajectory);
  console.log(`Original sampling rate: ${samplingRate.toFixed(1)}Hz`);
  
  // 2. Resample if needed
  let trajectory = rawTrajectory;
  if (samplingRate > 35 || samplingRate < 25) {
    trajectory = resampleTo30Hz(rawTrajectory);
    console.log(`Resampled to 30Hz (${trajectory.length} points)`);
  }
  
  // 3. Check for missing data
  const expectedPoints = (trajectory[trajectory.length - 1].timestamp - trajectory[0].timestamp) / 33.33;
  const missingPoints = countMissingPoints(trajectory);
  const missingDataRatio = missingPoints / expectedPoints;
  
  console.log(`Missing data ratio: ${(missingDataRatio * 100).toFixed(1)}%`);
  
  if (missingDataRatio > 0.2) {
    console.warn('Too much missing data, excluding trajectory');
    return null;
  }
  
  // 4. Apply smoothing
  const smoothed = applyRollingAverage(trajectory, 100);
  
  return smoothed;
}

// Additional preprocessing utilities

// Calculate velocity and acceleration
export function calculateKinematics(trajectory: MousePoint[]) {
  if (trajectory.length < 3) return null;
  
  const velocities: number[] = [];
  const accelerations: number[] = [];
  
  // Calculate velocities
  for (let i = 1; i < trajectory.length; i++) {
    const dt = (trajectory[i].timestamp - trajectory[i-1].timestamp) / 1000; // Convert to seconds
    const dx = trajectory[i].x - trajectory[i-1].x;
    const dy = trajectory[i].y - trajectory[i-1].y;
    const velocity = Math.sqrt(dx * dx + dy * dy) / dt;
    velocities.push(velocity);
  }
  
  // Calculate accelerations
  for (let i = 1; i < velocities.length; i++) {
    const dt = (trajectory[i+1].timestamp - trajectory[i].timestamp) / 1000;
    const acceleration = (velocities[i] - velocities[i-1]) / dt;
    accelerations.push(acceleration);
  }
  
  return {
    avgVelocity: velocities.length > 0 ? velocities.reduce((a, b) => a + b, 0) / velocities.length : 0,
    maxVelocity: velocities.length > 0 ? Math.max(...velocities) : 0,
    avgAcceleration: accelerations.length > 0 ? accelerations.reduce((a, b) => a + b, 0) / accelerations.length : 0,
    maxAcceleration: accelerations.length > 0 ? Math.max(...accelerations.map(Math.abs)) : 0
  };
}

// Detect pauses in movement (for hesitation analysis)
export function detectPauses(
  trajectory: MousePoint[], 
  pauseThresholdMs: number = 300,
  movementThresholdPx: number = 5
): number {
  let pauseCount = 0;
  let i = 0;
  
  while (i < trajectory.length - 1) {
    const startPoint = trajectory[i];
    let j = i + 1;
    
    // Find how long the mouse stays in roughly the same position
    while (j < trajectory.length) {
      const distance = Math.sqrt(
        Math.pow(trajectory[j].x - startPoint.x, 2) + 
        Math.pow(trajectory[j].y - startPoint.y, 2)
      );
      
      if (distance > movementThresholdPx) {
        break;
      }
      j++;
    }
    
    const pauseDuration = trajectory[Math.min(j, trajectory.length - 1)].timestamp - startPoint.timestamp;
    if (pauseDuration >= pauseThresholdMs) {
      pauseCount++;
    }
    
    i = Math.max(i + 1, j);
  }
  
  return pauseCount;
}

// Calculate trajectory complexity (for processing mode detection)
export function calculateTrajectoryComplexity(trajectory: MousePoint[]): number {
  if (trajectory.length < 2) return 0;
  
  // Calculate total distance
  let totalDistance = 0;
  for (let i = 1; i < trajectory.length; i++) {
    const dx = trajectory[i].x - trajectory[i-1].x;
    const dy = trajectory[i].y - trajectory[i-1].y;
    totalDistance += Math.sqrt(dx * dx + dy * dy);
  }
  
  // Calculate direct distance
  const directDistance = Math.sqrt(
    Math.pow(trajectory[trajectory.length - 1].x - trajectory[0].x, 2) +
    Math.pow(trajectory[trajectory.length - 1].y - trajectory[0].y, 2)
  );
  
  // Complexity is the ratio (1 = straight line, higher = more complex)
  return directDistance > 0 ? totalDistance / directDistance : 1;
}

// Export all preprocessing functions as a module
export const MousePreprocessor = {
  preprocess: preprocessMouseTrajectory,
  calculateSamplingRate,
  resampleTo30Hz,
  countMissingPoints,
  applyRollingAverage,
  calculateKinematics,
  detectPauses,
  calculateTrajectoryComplexity
};